'use client';

import { useEffect, useRef, useState, RefObject, CSSProperties } from "react";
import { secondsToHmsVerbose } from "@/helpers/helperFunctions";
// import { MobileDeviceContent } from "@/components/MobileDeviceContent";
import { TrainingModel } from "@/db/schema";
import { isMobile } from "react-device-detect";
import Link from "next/link";
import AnimatedTextWriter from "react-animated-text-writer";
import Moment from "moment";
import RadixTooltip from "react-radix-tooltip";
import DisplayProgressCircles from "@/components/DisplayProgressCircles";
import SvgIcons from "@/components/SvgIcons";
import useOnScreen from "@/helpers/useOnScreen";
import {
    MySkillsOnAIAndAutonomousAgents,
    MySkillsOnCloudAndDevOps,
    MySkillsOnDatabases,
    MySkillsOnFrameworksAndLibraries,
    MySkillsOnOtherTechnologies,
    MySkillsOnProgramming,
    MySkillsOnWebTechnologies
} from '@/components/Skills';
import { SkeletonSkillsAndTrainingsItems } from "@/components/Skeletons";


const {
    LinkExternalIcon,
    ListIcon,
    WorkIcon
} = SvgIcons();


export type TrainingTypes = "Publication" | "Training" | "Certification";
export const trainingTypeContextMap: Record<TrainingTypes, { prefix: string; color: string; text: string }> = {
    "Publication": { prefix: "Published On", color: 'var(--accent-third-color)', text: 'Publication' },
    "Training": { prefix: "", color: '', text: '' },
    "Certification": { prefix: "Issued On", color: 'var(--accent-fourth-color)', text: 'Certification Exam' },
};

export function MyOverallSkillsAndTrainings() {
    const [baseUrl, setBaseUrl] = useState<string | null>(null);
    const [width, setWidth] = useState(0);
    const [loadingTrainings, setLoadingTrainings] = useState<boolean>(true);
    const [loadingNextPage, setLoadingNextPage] = useState<boolean>(false);
    const [items, setItems] = useState<TrainingModel[]>([]);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [autoLoadNextPage, setAutoLoadNextPage] = useState<boolean>(false);
    const [icon, setIcon] = useState({ width: '30px', height: '30px' });
    const parentContainer = useRef<HTMLDivElement>(null);
    const moreButton = useRef<HTMLDivElement>(null);
    const isOnScreen = useOnScreen(moreButton as RefObject<HTMLElement>);

    useEffect(() => {
        setBaseUrl(window.location.origin);
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        handleResize();
        loadTrainings();
    }, []);

    useEffect(() => {
        loadTrainings();
    }, [baseUrl]);

    useEffect(() => {
        if (currentPage < totalPages) {
            setAutoLoadNextPage(isOnScreen);
        }
    }, [isOnScreen]);

    useEffect(() => {
        if (autoLoadNextPage && !loadingNextPage) {
            loadTrainings(true);
        }
    }, [autoLoadNextPage]);

    const handleResize = () => {
        setWidth(window.innerWidth);
    }

    const loadTrainings = (nextPage = false) => {
        if (baseUrl === null) {
            return;
        }

        if (nextPage) {
            setLoadingNextPage(true);
        }

        const url = `${baseUrl ? `${baseUrl}/api/` : process.env.NEXT_PUBLIC_API_URL}trainings${`?page=${nextPage ? currentPage + 1 : currentPage}&rows=${rowsPerPage}`}`;
        fetch(url).then((res) => { return res.json(); }).then(async (data) => {
            if (items.length > 0 && data.page > currentPage) {
                setItems([...items, ...data.items]);
            } else {
                setItems(data.items);
            }

            if (data.responseCode === 1) {
                setLoadingTrainings(false);
                setCurrentPage(data.page ?? 1);
                setTotalPages(data.totalPages ?? 0);
                setAutoLoadNextPage(false);
                setLoadingNextPage(false);
            } else {
                setLoadingTrainings(false);
            }
        })
            .catch((error) => {
                console.error("Error during form submission:", error);
            });
    }


    return (
        <div className="skillsContainer max-w-350 mx-auto my-5 px-6 py-6 bg-(--card-bg-color) rounded-xl shadow-lg">
            <h1 className="text-center text-3xl font-bold mb-6 mt-0 text-white">Core Skills</h1>
            <MySkillsOnProgramming width={width} icon={icon} />
            <MySkillsOnFrameworksAndLibraries width={width} icon={icon} />
            <MySkillsOnCloudAndDevOps width={width} icon={icon} />
            <div className="flex flex-col md:flex-row md:gap-4">
                <div className="grow">
                    <MySkillsOnDatabases width={width} icon={icon} />
                </div>
                <div className="grow">
                    <MySkillsOnAIAndAutonomousAgents width={width} icon={icon} />
                </div>
            </div>
            <MySkillsOnWebTechnologies width={width} icon={icon} />
            <MySkillsOnOtherTechnologies width={width} icon={icon} />

            <h1 className="text-center text-3xl font-bold mb-12 mt-15 text-white">Publications, Trainings and Certifications</h1>
            {loadingTrainings ? <SkeletonSkillsAndTrainingsItems /> : (
                // Render the trainings and certifications here when not loading
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-y-5 gap-x-6" ref={parentContainer}>
                    {items.length === 0 && (<p style={{ textAlign: 'center' }}>No publications, trainings or certifications available.</p>)}
                    {items.map((item, index) => <DisplayTrainingItem item={item} key={index} />)}
                    {currentPage < totalPages && (
                        <div className="col-span-1 xl:col-span-2" style={{ textAlign: 'center' }} >
                            <button className="button" onClick={() => {
                                loadTrainings(true);
                            }}>
                                <div className="flex flex-row items-center justify-center gap-4">
                                    {loadingNextPage ? 'Loading' : 'More'} {loadingNextPage && (<DisplayProgressCircles n={1} style={{ width: '12px', height: '12px' }} />)}
                                </div>
                            </button>
                        </div>
                    )}
                </div>
            )}
            <div ref={moreButton}></div>
            {isMobile && (
                <div style={{ marginLeft: -5, marginRight: -5, marginTop: 20, marginBottom: 50 }}>
                    <Link
                        href={'/work'}
                        className="button" style={{ float: 'left' }}
                        prefetch={true}
                    >
                        <span className="symbol prev">&laquo;</span>
                        Work
                    </Link>
                    <Link
                        href={'/games'}
                        className="button" style={{ float: 'right' }}
                        prefetch={true}
                    >
                        Games
                        <span className="symbol next">&raquo;</span>
                    </Link>
                </div>
            )}
            <div className={`buttonContainer fixed right-5 bottom-6 transition-all duration-1000 z-200 m-0! w-auto! p-0!`}>
                <Link
                    href={`/work-with-me?url=${encodeURIComponent(`${baseUrl}/skills-and-trainings`)}`}
                    className={`button mx-auto! gap-6! work-with-me-small`}
                    prefetch={true}
                >
                    <WorkIcon width='32px' height='32px' style={{ margin: '-10px' }} />
                    Like what you see? → Work with Me
                </Link>
            </div>
        </div>
    );
}

const DisplayTrainingItem = ({ item }: { item: TrainingModel }) => {
    let date = Moment(item.edate).format('MMMM D, Y');
    const duration = secondsToHmsVerbose((item.duration ?? 0) * 60, 'hour', 'min');

    if (item.displayonlyyear === 1) {
        date = Moment(item.edate).format('Y');
    } else if (item.hidedate === 1) {
        date = Moment(item.edate).format('MMMM Y');
    }
    return (
        <div className="trainingItem">
            <h3>
                {item.title}
                {item.certificate_url && (
                    <a href={item.certificate_url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '8px' }}>
                        <LinkExternalIcon width="20px" height="20px" />
                    </a>
                )}
            </h3>
            <div>
                <h4>
                    {item.institution}
                    <DisplayTrainingTooltipContent description={item.description} />
                </h4>
                <p>
                    <span>
                        {trainingTypeContextMap[item.type as TrainingTypes].prefix ? <b>{trainingTypeContextMap[item.type as TrainingTypes].prefix}: </b> : ''}
                        {date}
                        {trainingTypeContextMap[item.type as TrainingTypes].text && <span style={{ color: trainingTypeContextMap[item.type as TrainingTypes].color }}> ({trainingTypeContextMap[item.type as TrainingTypes].text})</span>}
                    </span>
                    <span>{duration}</span>
                </p>
            </div>
        </div>
    );
}

const DisplayTrainingTooltipContent = ({ description }: { description?: string }) => {
    if (!description || description.length === 0) return null;

    let leftPart = description;
    let rightPart = '';

    const length = description.length;
    if (length > 200) {
        const half = Math.floor(length * 2 / 3);
        leftPart = description.substring(0, half).replaceAll('\n', '<br />');
        rightPart = description.substring(half, length).replaceAll('\n', '<br />');
    }

    return (
        <div style={{ cursor: 'pointer' }}>
            <RadixTooltip
                title={<AnimatedTextWriter delay={25} content={rightPart} displayCodeWrapper={false} prefix={`<div class="trainingDetails"><h5>Details</h5>${leftPart}`} suffix="</div>" replacablePrefix='...' />}
                placement="bottom"
                enableForMobile={true}
                enableForTouch={true}
                style={{ maxWidth: '600px', minWidth: '400px' }}
            >
                <div>
                    <ListIcon width="16px" height="16px" />
                </div>
            </RadixTooltip>
        </div>
    );
}

